import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import './components.css';

export type MeterTone = 'accent' | 'warn' | 'danger' | 'neutral';
/**
 * Barra de uso (0–100%). A cor do preenchimento carrega a severidade e o trilho é o tom suave da mesma cor,
 * para o estado ser lido na barra inteira. O número fica sempre em texto ao lado (a cor nunca vai sozinha).
 */
export function Meter({ pct, tone = 'accent', label }: { pct: number; tone?: MeterTone; label?: string }) {
  const p = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
  return (
    <div className={`k-meter k-meter--${tone}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p)} aria-label={label}>
      <div className="k-meter__fill" style={{ width: `${p}%` }} />
    </div>
  );
}

export interface LinePoint { t: number; value: number | null }
/** Faixa horizontal de fundo (ex.: zona amarela/vermelha de um limite). */
export interface ChartBand { from: number; to: number; tone: 'warn' | 'danger' }
/** Linha fina horizontal de referência (ex.: o próprio limite), com rótulo opcional. */
export interface ChartRule { value: number; tone: 'warn' | 'danger'; label?: string }
export interface LineChartProps {
  points: LinePoint[];
  formatValue: (v: number) => string;
  /** Texto do tooltip para o instante do ponto. */
  formatTime: (t: number) => string;
  /** Rótulo curto do eixo X; padrão = formatTime. */
  formatTick?: (t: number) => string;
  height?: number;
  bands?: ChartBand[];
  rules?: ChartRule[];
  yMin?: number;
  yMax?: number;
  ariaLabel: string;
  /** Mantém o desenho anterior esmaecido enquanto recarrega. */
  loading?: boolean;
  empty?: ReactNode;
}

const M = { top: 12, right: 14, bottom: 26, left: 52 };

function niceStep(raw: number): number {
  if (!(raw > 0)) return 1;
  const exp = Math.floor(Math.log10(raw));
  const f = raw / 10 ** exp;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nf * 10 ** exp;
}

/**
 * Gráfico de linha de uma série (2px, eixos em linha fina), com faixas de limite opcionais, mira vertical e
 * tooltip no hover/teclado. A série usa a tinta neutra: verde/amarelo/vermelho ficam reservados para o estado.
 */
export function LineChart({ points, formatValue, formatTime, formatTick, height = 220, bands = [], rules = [], yMin, yMax, ariaLabel, loading, empty }: LineChartProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  const hasData = points.some((p) => p.value != null);
  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => { const w = entries[0]?.contentRect.width; if (w && w > 0) setWidth(Math.round(w)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasData]);

  const sorted = useMemo(() => [...points].sort((a, b) => a.t - b.t), [points]);
  const valid = useMemo(() => sorted.map((p, i) => ({ ...p, i })).filter((p): p is { t: number; value: number; i: number } => p.value != null && Number.isFinite(p.value)), [sorted]);

  const geo = useMemo(() => {
    const plotW = Math.max(40, width - M.left - M.right);
    const plotH = Math.max(40, height - M.top - M.bottom);
    const vals = valid.map((p) => p.value);
    const refs = rules.map((r) => r.value);
    let lo = yMin ?? Math.min(...vals, ...refs);
    let hi = yMax ?? Math.max(...vals, ...refs);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 1; }
    if (lo === hi) { const pad = Math.abs(lo) * 0.1 || 1; lo -= pad; hi += pad; }
    const step = niceStep((hi - lo) / 4);
    lo = Math.floor(lo / step) * step;
    hi = Math.ceil(hi / step) * step;
    const yTicks: number[] = [];
    for (let v = lo; v <= hi + step / 2; v += step) yTicks.push(Number(v.toFixed(10)));
    const ts = sorted.map((p) => p.t);
    let t0 = Math.min(...ts);
    let t1 = Math.max(...ts);
    if (!Number.isFinite(t0) || !Number.isFinite(t1)) { t0 = 0; t1 = 1; }
    if (t0 === t1) { t0 -= 3_600_000; t1 += 3_600_000; }
    const x = (t: number) => M.left + ((t - t0) / (t1 - t0)) * plotW;
    const y = (v: number) => M.top + (1 - (v - lo) / (hi - lo)) * plotH;
    const nx = Math.max(2, Math.min(7, Math.floor(plotW / 110)));
    const xTicks = Array.from({ length: nx }, (_, k) => t0 + ((t1 - t0) * k) / (nx - 1));
    return { plotW, plotH, lo, hi, yTicks, x, y, xTicks };
  }, [width, height, valid, sorted, rules, yMin, yMax]);

  const path = useMemo(() => {
    let d = '';
    let pen = false;
    for (const p of sorted) {
      if (p.value == null || !Number.isFinite(p.value)) { pen = false; continue; }
      d += `${pen ? 'L' : 'M'}${geo.x(p.t).toFixed(1)},${geo.y(p.value).toFixed(1)}`;
      pen = true;
    }
    return d;
  }, [sorted, geo]);

  if (valid.length === 0) return <div ref={wrap} className="k-chart k-chart--empty" style={{ minHeight: height }}>{empty}</div>;

  const clampY = (v: number) => Math.max(geo.lo, Math.min(geo.hi, v));
  const nearest = (px: number) => {
    let best = valid[0]!;
    for (const p of valid) if (Math.abs(geo.x(p.t) - px) < Math.abs(geo.x(best.t) - px)) best = p;
    return valid.indexOf(best);
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover(nearest(((e.clientX - r.left) / r.width) * width));
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const cur = hover ?? (e.key === 'ArrowRight' ? -1 : valid.length);
      setHover(Math.max(0, Math.min(valid.length - 1, cur + (e.key === 'ArrowRight' ? 1 : -1))));
    } else if (e.key === 'Escape') setHover(null);
  };
  const hp = hover != null ? valid[hover] : undefined;
  const last = valid[valid.length - 1]!;
  const tick = formatTick ?? formatTime;
  const hx = hp ? geo.x(hp.t) : 0;
  return (
    <div ref={wrap} className={`k-chart ${loading ? 'k-chart--loading' : ''}`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel} tabIndex={0} onPointerMove={onMove} onPointerLeave={() => setHover(null)} onKeyDown={onKey} onBlur={() => setHover(null)}>
        {bands.map((b, i) => { const a = geo.y(clampY(Math.max(b.from, b.to))); const z = geo.y(clampY(Math.min(b.from, b.to))); return z - a > 0 ? <rect key={i} className={`k-chart__band k-chart__band--${b.tone}`} x={M.left} y={a} width={geo.plotW} height={z - a} /> : null; })}
        {geo.yTicks.map((v) => <g key={v}><line className="k-chart__grid" x1={M.left} x2={M.left + geo.plotW} y1={geo.y(v)} y2={geo.y(v)} /><text x={M.left - 8} y={geo.y(v)} dy="0.32em" textAnchor="end">{formatValue(v)}</text></g>)}
        {geo.xTicks.map((t, i) => <text key={i} x={geo.x(t)} y={height - 8} textAnchor={i === 0 ? 'start' : i === geo.xTicks.length - 1 ? 'end' : 'middle'}>{tick(t)}</text>)}
        {rules.filter((r) => r.value >= geo.lo && r.value <= geo.hi).map((r, i) => <g key={i}><line className={`k-chart__rule k-chart__rule--${r.tone}`} x1={M.left} x2={M.left + geo.plotW} y1={geo.y(r.value)} y2={geo.y(r.value)} />{r.label && <text x={M.left + 6} y={geo.y(r.value) - 4} textAnchor="start" className="k-chart__rule-label">{r.label}</text>}</g>)}
        <path className="k-chart__line" d={path} />
        {!hp && <circle className="k-chart__dot" cx={geo.x(last.t)} cy={geo.y(last.value)} r={4} />}
        {hp && <><line className="k-chart__crosshair" x1={hx} x2={hx} y1={M.top} y2={M.top + geo.plotH} /><circle className="k-chart__dot" cx={hx} cy={geo.y(hp.value)} r={4} /></>}
      </svg>
      {hp && (
        <div className="k-chart__tip" style={hx > width / 2 ? { right: width - hx + 10 } : { left: hx + 10 }} aria-live="polite">
          <strong>{formatValue(hp.value)}</strong>
          <span>{formatTime(hp.t)}</span>
        </div>
      )}
    </div>
  );
}
